import { Extension } from "@tiptap/core"
import { Mark } from "@tiptap/pm/model"
import { Plugin, PluginKey } from "prosemirror-state"

export const ClearStoredLink = Extension.create({
  name: "clearStoredLink",

  addProseMirrorPlugins() {
    const getLinkMarkAtCursor = (state): Mark | null => {
      const linkMarkType = state.schema.marks.link
      if (!linkMarkType) return null

      const { from, $from } = state.selection

      const marksAtCursor = $from.marks()
      const markAtCursor = marksAtCursor.find(mark => mark.type === linkMarkType)
      if (markAtCursor) return markAtCursor

      const nodeBeforeMarks = $from.nodeBefore?.marks ?? []
      const markBefore = nodeBeforeMarks.find(mark => mark.type === linkMarkType)
      if (markBefore) return markBefore

      const nodeAfterMarks = $from.nodeAfter?.marks ?? []
      const markAfter = nodeAfterMarks.find(mark => mark.type === linkMarkType)
      if (markAfter) return markAfter

      const resolvedBefore = from > 0 ? state.doc.resolve(from - 1).marks() : []
      const markResolvedBefore = resolvedBefore.find(mark => mark.type === linkMarkType)
      if (markResolvedBefore) return markResolvedBefore

      return null
    }

    const getLinkRangeAtCursor = (
      state
    ): { from: number; to: number; mark: Mark; text: string } | null => {
      const linkMarkType = state.schema.marks.link
      if (!linkMarkType) return null

      const mark = getLinkMarkAtCursor(state)
      if (!mark) return null

      const { from } = state.selection
      const docSize = state.doc.content.size

      let start = from
      let end = from

      while (
        start > 0 &&
        state.doc
          .resolve(start - 1)
          .marks()
          .some(currentMark => currentMark.type === linkMarkType)
      ) {
        start -= 1
      }

      while (
        end < docSize &&
        state.doc
          .resolve(end)
          .marks()
          .some(currentMark => currentMark.type === linkMarkType)
      ) {
        end += 1
      }

      if (start === end) return null

      return {
        from: start,
        to: end,
        mark,
        text: state.doc.textBetween(start, end, "\0", "\0")
      }
    }

    return [
      new Plugin({
        key: new PluginKey("clearStoredLink"),
        appendTransaction(transactions, oldState, newState) {
          const hasRelevantChange = transactions.some(tr => tr.docChanged || tr.selectionSet)
          if (!hasRelevantChange) return

          const linkMarkType = newState.schema.marks.link
          if (!linkMarkType) return

          const { selection } = newState
          if (!selection.empty) return

          const { from } = selection
          const textBefore = from > 0 ? newState.doc.textBetween(from - 1, from, "\0", "\0") : ""
          const endsWithWhitespace = /\s/.test(textBefore)

          if (endsWithWhitespace) {
            const tr = newState.tr

            if (from > 0) {
              tr.removeMark(from - 1, from, linkMarkType)
            }

            tr.removeStoredMark(linkMarkType)
            tr.setStoredMarks([])

            return tr
          }

          const oldLinkRange = getLinkRangeAtCursor(oldState)
          const newLinkRange = getLinkRangeAtCursor(newState)

          if (oldLinkRange && newLinkRange) {
            const oldHref = oldLinkRange.mark.attrs?.href ?? ""
            const oldText = oldLinkRange.text
            const newHref = newLinkRange.mark.attrs?.href ?? ""
            const newText = newLinkRange.text

            if (oldHref === oldText && newText && !/\s$/.test(newText) && newHref !== newText) {
              const tr = newState.tr
              tr.removeMark(newLinkRange.from, newLinkRange.to, linkMarkType)
              tr.addMark(
                newLinkRange.from,
                newLinkRange.to,
                linkMarkType.create({
                  ...newLinkRange.mark.attrs,
                  href: newText
                })
              )

              const storedLink = newState.storedMarks?.find(mark => mark.type === linkMarkType)
              if (storedLink) {
                tr.removeStoredMark(linkMarkType)
                tr.addStoredMark(
                  linkMarkType.create({
                    ...storedLink.attrs,
                    href: newText
                  })
                )
              }

              return tr
            }
          }

          const currentStoredLink = newState.storedMarks?.find(mark => mark.type === linkMarkType)
          const { $from: $newFrom } = newState.selection
          const activeLinkMark =
            $newFrom.marks().find(mark => mark.type === linkMarkType) ??
            ($newFrom.nodeBefore?.marks ?? []).find(mark => mark.type === linkMarkType) ??
            (from > 0
              ? newState.doc
                  .resolve(from - 1)
                  .marks()
                  .find(mark => mark.type === linkMarkType)
              : null) ??
            null

          if (!activeLinkMark) {
            if (currentStoredLink) {
              const tr = newState.tr
              tr.removeStoredMark(linkMarkType)
              tr.setStoredMarks([])
              return tr
            }
            return
          }

          if (!currentStoredLink) {
            return newState.tr.addStoredMark(activeLinkMark)
          }

          const currentHref = currentStoredLink.attrs?.href ?? ""
          const activeHref = activeLinkMark.attrs?.href ?? ""

          if (currentHref !== activeHref) {
            const tr = newState.tr
            tr.removeStoredMark(linkMarkType)
            tr.addStoredMark(activeLinkMark)
            return tr
          }
        }
      })
    ]
  }
})
